/**
 * MobileOpportunitiesView
 *
 * Mobile wrapper for the Opportunities module.
 * Renders when isMobile === 2, replacing the desktop OpportunitiesView.
 * Two tabs: Opportunities (always) + Ending Contracts (owners only).
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Tabs, Tab, Typography, IconButton } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Langs, { Localize } from "../../../app/lang/langs";
import { isOwner, checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import MobileOpportunitiesTab from "./MobileOpportunitiesTab";
import MobileEndingContractsTab from "./MobileEndingContractsTab";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileOpportunitiesView() {
  const navigate = useNavigate();
  const urlParams = useParams();

  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const userRoleId = useSelector((state) => state.login.userRoleId);

  const [selectedView, setSelectedView] = useState(0);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const showEndingContracts = isOwner(userRoleId);
  const canEdit = checkPermission(config.scopes.opportunities, config.permissionTypes.edit);

  // Sync view from URL
  useEffect(() => {
    if (urlParams.subView !== undefined) {
      const view = parseInt(urlParams.subView, 10);
      if (view === 0 || (view === 1 && showEndingContracts)) {
        setSelectedView(view);
      }
    }
  }, [urlParams, showEndingContracts]);

  /* ---- Handlers ---- */
  const handleViewChange = (_event, newValue) => {
    if (newValue !== null) {
      const selectedTeamId = selectedTeamsIds[0];
      navigate(`/opportunities/${selectedTeamId}/${newValue}`);
    }
  };

  /* ---- Render ---- */
  return (
    <Box
      sx={{
        mt: 0,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
      }}
    >
      {/* ---- Header section (glassmorphic) ---- */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0,
          flexShrink: 0,
          backgroundColor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(24px) saturate(1.5)",
          WebkitBackdropFilter: "blur(24px) saturate(1.5)",
          position: "relative",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
          },
        }}
      >
        {/* Title + Add button */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Langs str="OPPORTUNITIES" />
          </Typography>
          {canEdit && selectedView === 0 && (
            <IconButton
              onClick={() => setAddModalVisible(true)}
              sx={{
                minWidth: 44,
                minHeight: 44,
                borderRadius: "14px",
                color: "rgba(0,0,0,0.6)",
                transition: TRANSITION_SMOOTH,
                backdropFilter: "blur(8px)",
                "&:hover": {
                  backgroundColor: "rgba(255,144,21,0.1)",
                  color: "rgb(255,144,21)",
                  boxShadow: "0 4px 16px rgba(255,144,21,0.2), 0 1px 4px rgba(255,144,21,0.08)",
                  transform: "scale(1.08)",
                },
                "&:active": {
                  transform: "scale(0.94)",
                  boxShadow: "0 1px 4px rgba(255,144,21,0.08)",
                },
              }}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          )}
        </Box>

        {/* Tabs */}
        {showEndingContracts ? (
          <Tabs
            value={selectedView}
            onChange={handleViewChange}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              bgcolor: "rgba(0,0,0,0.04)",
              borderRadius: "14px",
              p: 0.5,
              "& .MuiTabs-indicator": {
                height: "100%",
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                zIndex: 0,
                boxShadow: "0 2px 12px rgba(255,144,21,0.3)",
              },
              "& .MuiTab-root": {
                minHeight: 36,
                fontSize: "0.82rem",
                fontWeight: 600,
                textTransform: "none",
                zIndex: 1,
                color: "text.secondary",
                transition: TRANSITION_SMOOTH,
                borderRadius: "10px",
                "&.Mui-selected": {
                  color: "#fff",
                },
              },
            }}
          >
            <Tab label={Localize("OPPORTUNITIES")} value={0} />
            <Tab label={Localize("ENDING_CONTRACTS")} value={1} />
          </Tabs>
        ) : null}
      </Box>

      {/* ---- Body ---- */}
      <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {selectedView === 0 && <MobileOpportunitiesTab addModalVisible={addModalVisible} setAddModalVisible={setAddModalVisible} />}
        {selectedView === 1 && showEndingContracts && <MobileEndingContractsTab />}
      </Box>
    </Box>
  );
}
